import * as React from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import type { BillingCardSetupDto } from '@lib/dto/billing.dto'
import { formatCents } from '@lib/helpers/money'
import { uuidParamSchema } from '@lib/validators/params'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { HandoffLoading, HandoffSignIn } from '@/components/billing/HandoffShell'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { tenantApi } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { billingKeys } from '@/hooks/queryKeys'
import { useHandoffCancel } from '@/hooks/useHandoffCancel'
import { BillingService } from '@/services/BillingService'

const service = new BillingService(tenantApi)

/** The auto-reload settings saving the card turns on, in words. */
function settingsLine(setup: BillingCardSetupDto): string | null {
  const settings = setup.pendingAutoReload
  if (!settings) return null
  return `When the balance falls below ${formatCents(settings.triggerCents)}, add ${formatCents(settings.amountCents)} (plus tax), up to ${formatCents(settings.monthlyCapCents)} a month.`
}

function Settled({ setup }: { setup: BillingCardSetupDto }) {
  if (setup.status === 'succeeded') {
    return (
      <AuthShell
        title="Card saved"
        description={`${setup.pendingAutoReload ? 'Auto-reload is on. ' : ''}You can return to your AI app.`}
        size="md"
      >
        {null}
      </AuthShell>
    )
  }
  return <AuthShell title="The card wasn’t saved" description="Auto-reload wasn’t changed. You can return to your AI app." size="md">{null}</AuthShell>
}

/**
 * PixelSign's page for saving a card for auto-reload when an AI app asked. It
 * shows the settings that saving the card turns on (saving it is the person's
 * confirmation of them) and hands them to Stripe; opening it saves nothing.
 */
export function BillingCardPage() {
  const { tenantId: rawTenantId, setupId: rawSetupId } = useParams()
  const parsedTenantId = uuidParamSchema.safeParse(rawTenantId)
  const parsedSetupId = uuidParamSchema.safeParse(rawSetupId)
  const tenantId = parsedTenantId.success ? parsedTenantId.data : null
  const setupId = parsedSetupId.success ? parsedSetupId.data : null
  const [params] = useSearchParams()
  const saved = params.get('saved') === '1'
  const cancelled = params.get('cancelled') === '1'
  const tenant = useTenantAuth()
  const organization = tenant.session?.memberships.find((membership) => membership.tenantId === tenantId)?.tenantName ?? null
  const query = useQuery({
    queryKey: billingKeys.cardSetup(tenantId, 'tenant', setupId),
    queryFn: () => {
      if (tenantId === null || setupId === null) throw new Error('Card link is not valid')
      return service.cardSetup(tenantId, setupId)
    },
    enabled: tenant.isAuthenticated && !cancelled && tenantId !== null && setupId !== null,
    refetchInterval: (current) => (saved && current.state.data?.status === 'pending' ? 2_000 : false),
    retry: false,
  })
  const cancel = useHandoffCancel(tenantId, setupId, {
    returnedCancelled: cancelled,
    signedIn: tenant.isAuthenticated,
    send: (target) => service.cancelCardSetup(target.tenantId, target.id),
  })
  const [leaving, setLeaving] = React.useState(false)

  if (tenantId === null || setupId === null) {
    return (
      <AuthShell title="Save a card" size="md">
        <ErrorState message="That card link is not valid." />
      </AuthShell>
    )
  }

  if (tenant.isLoading) return <HandoffLoading />
  if (!tenant.isAuthenticated) return <HandoffSignIn title="Save a card" />
  // A cancel from this page's button shows its outcome too, not only Stripe's back link.
  const setup = cancel.result ?? (cancelled ? undefined : query.data)
  const failure = cancelled ? cancel.error : query.error
  if (failure) {
    return (
      <AuthShell title="Save a card" size="md">
        <ErrorState message={apiErrorMessage(failure, 'Could not load this page.')} />
      </AuthShell>
    )
  }
  if (!setup) return <HandoffLoading />
  if (setup.status !== 'pending') return <Settled setup={setup} />
  if (saved) return <AuthShell title="Saving your card…" description="This takes a few seconds." size="md">{null}</AuthShell>

  const checkoutUrl = setup.checkoutUrl
  const line = settingsLine(setup)
  return (
    <AuthShell title={organization ? `Auto-reload for ${organization}` : 'Auto-reload'} size="md">
      <div className="space-y-4">
        {line ? <p className="text-body">{line}</p> : null}
        {cancel.error ? <ErrorState message={apiErrorMessage(cancel.error, 'Could not cancel. Try again.')} /> : null}
        <p className="text-caption text-muted-foreground">
          You’ll save a card on Stripe’s page; nothing is charged now. Saving it turns these settings on. Your AI app asked for this; if you didn’t, cancel.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" disabled={cancel.isPending || leaving} onClick={cancel.cancel}>
            Cancel
          </Button>
          <Button
            disabled={!checkoutUrl || leaving}
            onClick={() => {
              if (!checkoutUrl) return
              setLeaving(true)
              window.location.assign(checkoutUrl)
            }}
          >
            Save a card
          </Button>
        </div>
      </div>
    </AuthShell>
  )
}
